import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Footer } from '@/components/Footer'
import { Header } from '@/components/Header'
import { ARTICLES, getArticle } from '@/content/articles.mjs'
import { articleJsonLd, getPage } from '@/content/seoPages.mjs'
import { applyPageMeta, SITE_ORIGIN } from '@/lib/seo'
import NotFound from '@/pages/NotFound'

const ctaClass =
  'inline-flex min-h-[52px] items-center justify-center rounded-full px-8 text-base font-bold tracking-[-0.01em] text-black shadow-[0_14px_36px_-10px_rgba(113,205,217,0.65),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] focus-visible:ring-offset-2 focus-visible:ring-offset-black'

const ctaStyle = {
  background: 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 60%, #5dbac6 100%)',
}

const kickerClass = 'text-[11px] font-semibold uppercase leading-none tracking-[0.22em] text-[#71cdd9]'

function formatDate(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function setArticleJsonLd(data) {
  let tag = document.getElementById('seo-article')
  if (!data) {
    tag?.remove()
    return
  }
  if (!tag) {
    tag = document.createElement('script')
    tag.type = 'application/ld+json'
    tag.id = 'seo-article'
    document.head.appendChild(tag)
  }
  tag.textContent = JSON.stringify(data)
}

function Block({ block }) {
  if (typeof block === 'string') return <p>{block}</p>
  if (block.list) {
    return (
      <ul className="list-disc space-y-2 pl-5 marker:text-[#71cdd9]">
        {block.list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )
  }
  if (block.steps) {
    return (
      <ol className="list-decimal space-y-2 pl-5 marker:font-semibold marker:text-[#71cdd9]">
        {block.steps.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    )
  }
  return null
}

function Meta({ article }) {
  return (
    <p className="text-sm text-white/55">
      <time dateTime={article.published}>{formatDate(article.published)}</time>
      <span aria-hidden> · </span>
      {article.readMinutes} min read
    </p>
  )
}

export function BlogIndex() {
  const page = getPage('/blog')

  useEffect(() => {
    applyPageMeta({ title: page.title, description: page.description, path: page.path })
    setArticleJsonLd(null)
  }, [page])

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="mx-auto w-full max-w-[1000px] px-5 pt-28 pb-16 sm:px-8 sm:pt-32">
        <h1 className="text-[2.15rem] font-extrabold leading-[1.05] tracking-[-0.04em] text-balance sm:text-[2.6rem] lg:text-[3rem]">
          {page.h1}
        </h1>
        <p className="mt-3 max-w-[36rem] text-base leading-relaxed text-white/75">{page.lede}</p>

        <ul className="mt-10 grid gap-3">
          {ARTICLES.map((article) => (
            <li key={article.slug}>
              <Link
                to={`/blog/${article.slug}`}
                className="group block rounded-2xl border border-white/10 bg-[#07080c] p-5 transition-colors hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] sm:p-6"
              >
                <span className={kickerClass}>{article.kicker}</span>
                <h2 className="mt-3 text-lg font-extrabold leading-snug tracking-[-0.02em] text-balance group-hover:text-[#bdeef5] sm:text-xl">
                  {article.h1}
                </h2>
                <p className="mt-2 max-w-[60ch] text-sm leading-relaxed text-white/70 sm:text-base">
                  {article.description}
                </p>
                <div className="mt-3">
                  <Meta article={article} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <Footer />
    </div>
  )
}

export function BlogArticle() {
  const { slug } = useParams()
  const article = getArticle(slug)
  const page = article ? getPage(`/blog/${article.slug}`) : null

  useEffect(() => {
    if (!page) return undefined
    applyPageMeta({ title: page.title, description: page.description, path: page.path })
    setArticleJsonLd(articleJsonLd(page, `${SITE_ORIGIN}${page.path}`))
    return () => setArticleJsonLd(null)
  }, [page])

  if (!article) return <NotFound />

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main>
        <article className="mx-auto w-full max-w-[720px] px-5 pt-28 pb-12 sm:px-8 sm:pt-32">
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-white/55">
            <Link to="/blog" className="hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] rounded-sm">
              Blog
            </Link>
            <span aria-hidden> / </span>
            <span>{article.kicker}</span>
          </nav>
          <h1 className="text-[2rem] font-extrabold leading-[1.08] tracking-[-0.04em] text-balance sm:text-[2.5rem]">
            {article.h1}
          </h1>
          <div className="mt-4">
            <Meta article={article} />
          </div>
          <p className="mt-6 text-lg leading-relaxed text-white/85">{article.intro}</p>

          <div className="mt-2 space-y-4 text-[17px] leading-[1.7] text-white/75">
            {article.sections.map((section) => (
              <section key={section.h2} className="space-y-4">
                <h2 className="pt-6 text-[1.4rem] font-extrabold leading-tight tracking-[-0.03em] text-white sm:text-[1.6rem]">
                  {section.h2}
                </h2>
                {section.body.map((block, i) => (
                  <Block key={i} block={block} />
                ))}
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-[#07080c] p-6 sm:p-8">
            <p className="text-xl font-extrabold tracking-[-0.03em]">Get your stats and highlights</p>
            <p className="mt-2 text-base leading-relaxed text-white/70">
              The Statbox app is free on iOS and Android. Running a community, academy, or venue? Book a 30-minute demo.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/app-download" className={ctaClass} style={ctaStyle}>
                Download the app
              </Link>
              <Link
                to="/book-demo"
                className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-white/20 px-8 text-base font-bold tracking-[-0.01em] text-white hover:border-white/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              >
                Book a demo
              </Link>
            </div>
          </div>
        </article>

        <nav aria-label="Related" className="border-t border-white/10">
          <div className="mx-auto grid w-full max-w-[1240px] gap-2 px-5 py-6 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
            {article.related.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex min-h-12 items-center rounded-xl border border-white/10 px-4 text-base font-semibold tracking-[-0.02em] text-white/85 hover:border-white/30 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>
      </main>
      <Footer />
    </div>
  )
}
